import * as i0 from '@angular/core';
import { Directive, Optional, Self, Input, Injectable, PLATFORM_ID, Inject, SecurityContext, NgModule } from '@angular/core';
import * as i1 from '@ngbracket/ngx-layout/core';
import { BaseDirective2, StyleBuilder, SERVER_TOKEN, LAYOUT_CONFIG, CoreModule } from '@ngbracket/ngx-layout/core';
import * as i2 from '@angular/common';
import { NgClass, isPlatformServer, NgStyle } from '@angular/common';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { takeUntil } from 'rxjs/operators';
import * as i2$1 from '@angular/platform-browser';

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
let ClassDirective = /*#__PURE__*/(() => {
  class ClassDirective extends BaseDirective2 {
    /**
     * Capture class assignments so we cache the default classes
     * which are merged with activated styles and used as fallbacks.
     */
    set klass(val) {
      this.ngClassInstance.klass = val;
      this.setValue(val, '');
    }
    constructor(elementRef, styler, marshal, renderer2, ngClassInstance) {
      super(elementRef, null, styler, marshal);
      this.ngClassInstance = ngClassInstance;
      this.DIRECTIVE_KEY = 'ngClass';
      if (!this.ngClassInstance) {
        // Create an instance NgClass Directive instance only if `ngClass=""` has NOT been defined on
        // the same host element; since the responsive variations may be defined...
        this.ngClassInstance = new NgClass(elementRef, renderer2);
      }
      this.init();
      this.setValue('', '');
    }
    updateWithValue(value) {
      this.ngClassInstance.ngClass = value;
      this.ngClassInstance.ngDoCheck();
    }
    // ******************************************************************
    // Lifecycle Hooks
    // ******************************************************************
    /**
     * For ChangeDetectionStrategy.onPush and ngOnChanges() updates
     */
    ngDoCheck() {
      this.ngClassInstance.ngDoCheck();
    }
    static {
      this.ɵfac = function ClassDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ClassDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1.StyleUtils), i0.ɵɵdirectiveInject(i1.MediaMarshaller), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i2.NgClass, 10));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: ClassDirective,
        inputs: {
          klass: [0, "class", "klass"]
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return ClassDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const inputs$3 = ['ngClass', 'ngClass.xs', 'ngClass.sm', 'ngClass.md', 'ngClass.lg', 'ngClass.xl', 'ngClass.lt-sm', 'ngClass.lt-md', 'ngClass.lt-lg', 'ngClass.lt-xl', 'ngClass.gt-xs', 'ngClass.gt-sm', 'ngClass.gt-md', 'ngClass.gt-lg'];
const selector$3 = `
  [ngClass], [ngClass.xs], [ngClass.sm], [ngClass.md], [ngClass.lg], [ngClass.xl],
  [ngClass.lt-sm], [ngClass.lt-md], [ngClass.lt-lg], [ngClass.lt-xl],
  [ngClass.gt-xs], [ngClass.gt-sm], [ngClass.gt-md], [ngClass.gt-lg]
`;
/**
 * Directive to add responsive support for ngClass.
 * This maintains the core functionality of 'ngClass' and adds responsive API
 * Note: this class is a no-op when rendered on the server
 */
let DefaultClassDirective = /*#__PURE__*/(() => {
  class DefaultClassDirective extends ClassDirective {
    constructor() {
      super(...arguments);
      this.inputs = inputs$3;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵDefaultClassDirective_BaseFactory;
        return function DefaultClassDirective_Factory(__ngFactoryType__) {
          return (ɵDefaultClassDirective_BaseFactory || (ɵDefaultClassDirective_BaseFactory = i0.ɵɵgetInheritedFactory(DefaultClassDirective)))(__ngFactoryType__ || DefaultClassDirective);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DefaultClassDirective,
        selectors: [["", "ngClass", ""], ["", "ngClass.xs", ""], ["", "ngClass.sm", ""], ["", "ngClass.md", ""], ["", "ngClass.lg", ""], ["", "ngClass.xl", ""], ["", "ngClass.lt-sm", ""], ["", "ngClass.lt-md", ""], ["", "ngClass.lt-lg", ""], ["", "ngClass.lt-xl", ""], ["", "ngClass.gt-xs", ""], ["", "ngClass.gt-sm", ""], ["", "ngClass.gt-md", ""], ["", "ngClass.gt-lg", ""]],
        inputs: {
          ngClass: "ngClass",
          "ngClass.xs": "ngClass.xs",
          "ngClass.sm": "ngClass.sm",
          "ngClass.md": "ngClass.md",
          "ngClass.lg": "ngClass.lg",
          "ngClass.xl": "ngClass.xl",
          "ngClass.lt-sm": "ngClass.lt-sm",
          "ngClass.lt-md": "ngClass.lt-md",
          "ngClass.lt-lg": "ngClass.lt-lg",
          "ngClass.lt-xl": "ngClass.lt-xl",
          "ngClass.gt-xs": "ngClass.gt-xs",
          "ngClass.gt-sm": "ngClass.gt-sm",
          "ngClass.gt-md": "ngClass.gt-md",
          "ngClass.gt-lg": "ngClass.gt-lg"
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return DefaultClassDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
let ImgSrcStyleBuilder = /*#__PURE__*/(() => {
  class ImgSrcStyleBuilder extends StyleBuilder {
    buildStyles(url) {
      return {
        content: url ? `url(${url})` : ''
      };
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵImgSrcStyleBuilder_BaseFactory;
        return function ImgSrcStyleBuilder_Factory(__ngFactoryType__) {
          return (ɵImgSrcStyleBuilder_BaseFactory || (ɵImgSrcStyleBuilder_BaseFactory = i0.ɵɵgetInheritedFactory(ImgSrcStyleBuilder)))(__ngFactoryType__ || ImgSrcStyleBuilder);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ImgSrcStyleBuilder,
        factory: ImgSrcStyleBuilder.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ImgSrcStyleBuilder;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ImgSrcDirective = /*#__PURE__*/(() => {
  class ImgSrcDirective extends BaseDirective2 {
    set src(val) {
      this.defaultSrc = val;
      this.setValue(this.defaultSrc, '');
    }
    constructor(elementRef, styleBuilder, styler, marshal, platformId, serverModuleLoaded) {
      super(elementRef, styleBuilder, styler, marshal);
      this.platformId = platformId;
      this.serverModuleLoaded = serverModuleLoaded;
      this.DIRECTIVE_KEY = 'img-src';
      this.defaultSrc = '';
      this.styleCache = imgSrcCache;
      this.init();
      this.setValue(this.nativeElement.getAttribute('src') || '', '');
      if (isPlatformServer(this.platformId) && this.serverModuleLoaded) {
        this.nativeElement.setAttribute('src', '');
      }
    }
    /**
     * Use the [responsively] activated input value to update
     * the host img src attribute or assign a default `img.src=''`
     * if the src has not been defined.
     *
     * Do nothing to standard `<img src="">` usages, only when responsive
     * keys are present do we actually call `setAttribute()`
     */
    updateWithValue(value) {
      const url = value || this.defaultSrc;
      if (isPlatformServer(this.platformId) && this.serverModuleLoaded) {
        this.addStyles(url);
      } else {
        this.nativeElement.setAttribute('src', url);
      }
    }
    static {
      this.ɵfac = function ImgSrcDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ImgSrcDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(ImgSrcStyleBuilder), i0.ɵɵdirectiveInject(i1.StyleUtils), i0.ɵɵdirectiveInject(i1.MediaMarshaller), i0.ɵɵdirectiveInject(PLATFORM_ID), i0.ɵɵdirectiveInject(SERVER_TOKEN));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: ImgSrcDirective,
        inputs: {
          src: "src"
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return ImgSrcDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const imgSrcCache = new Map();
const inputs$2 = ['src.xs', 'src.sm', 'src.md', 'src.lg', 'src.xl', 'src.lt-sm', 'src.lt-md', 'src.lt-lg', 'src.lt-xl', 'src.gt-xs', 'src.gt-sm', 'src.gt-md', 'src.gt-lg'];
const selector$2 = `
  img[src.xs],    img[src.sm],    img[src.md],    img[src.lg],   img[src.xl],
  img[src.lt-sm], img[src.lt-md], img[src.lt-lg], img[src.lt-xl],
  img[src.gt-xs], img[src.gt-sm], img[src.gt-md], img[src.gt-lg]
`;
/**
 * This directive provides a responsive API for the HTML <img> 'src' attribute
 * and will update the img.src property upon each responsive activation.
 *
 * e.g.
 *      <img src="defaultScene.jpg" src.xs="mobileScene.jpg"></img>
 *
 * @see https://css-tricks.com/responsive-images-youre-just-changing-resolutions-use-src/
 */
let DefaultImgSrcDirective = /*#__PURE__*/(() => {
  class DefaultImgSrcDirective extends ImgSrcDirective {
    constructor() {
      super(...arguments);
      this.inputs = inputs$2;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵDefaultImgSrcDirective_BaseFactory;
        return function DefaultImgSrcDirective_Factory(__ngFactoryType__) {
          return (ɵDefaultImgSrcDirective_BaseFactory || (ɵDefaultImgSrcDirective_BaseFactory = i0.ɵɵgetInheritedFactory(DefaultImgSrcDirective)))(__ngFactoryType__ || DefaultImgSrcDirective);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DefaultImgSrcDirective,
        selectors: [["img", "src.xs", ""], ["img", "src.sm", ""], ["img", "src.md", ""], ["img", "src.lg", ""], ["img", "src.xl", ""], ["img", "src.lt-sm", ""], ["img", "src.lt-md", ""], ["img", "src.lt-lg", ""], ["img", "src.lt-xl", ""], ["img", "src.gt-xs", ""], ["img", "src.gt-sm", ""], ["img", "src.gt-md", ""], ["img", "src.gt-lg", ""]],
        inputs: {
          "src.xs": "src.xs",
          "src.sm": "src.sm",
          "src.md": "src.md",
          "src.lg": "src.lg",
          "src.xl": "src.xl",
          "src.lt-sm": "src.lt-sm",
          "src.lt-md": "src.lt-md",
          "src.lt-lg": "src.lt-lg",
          "src.lt-xl": "src.lt-xl",
          "src.gt-xs": "src.gt-xs",
          "src.gt-sm": "src.gt-sm",
          "src.gt-md": "src.gt-md",
          "src.gt-lg": "src.gt-lg"
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return DefaultImgSrcDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
let ShowHideStyleBuilder = /*#__PURE__*/(() => {
  class ShowHideStyleBuilder extends StyleBuilder {
    buildStyles(show, parent) {
      const shouldShow = show === 'true';
      return {
        display: shouldShow ? parent.display || (parent.isServer ? 'initial' : '') : 'none'
      };
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵShowHideStyleBuilder_BaseFactory;
        return function ShowHideStyleBuilder_Factory(__ngFactoryType__) {
          return (ɵShowHideStyleBuilder_BaseFactory || (ɵShowHideStyleBuilder_BaseFactory = i0.ɵɵgetInheritedFactory(ShowHideStyleBuilder)))(__ngFactoryType__ || ShowHideStyleBuilder);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ShowHideStyleBuilder,
        factory: ShowHideStyleBuilder.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ShowHideStyleBuilder;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ShowHideDirective = /*#__PURE__*/(() => {
  class ShowHideDirective extends BaseDirective2 {
    constructor(elementRef, styleBuilder, styler, marshal, layoutConfig, platformId, serverModuleLoaded) {
      super(elementRef, styleBuilder, styler, marshal);
      this.layoutConfig = layoutConfig;
      this.platformId = platformId;
      this.serverModuleLoaded = serverModuleLoaded;
      this.DIRECTIVE_KEY = 'show-hide';
      /** Original DOM Element CSS display style */
      this.display = '';
      this.hasLayout = false;
      this.hasFlexChild = false;
    }
    // *********************************************
    // Lifecycle Methods
    // *********************************************
    ngAfterViewInit() {
      this.trackExtraTriggers();
      const children = Array.from(this.nativeElement.children);
      for (let i = 0; i < children.length; i++) {
        if (this.marshal.hasValue(children[i], 'flex')) {
          this.hasFlexChild = true;
          break;
        }
      }
      if (DISPLAY_MAP.has(this.nativeElement)) {
        this.display = DISPLAY_MAP.get(this.nativeElement);
      } else {
        this.display = this.getDisplayStyle();
        DISPLAY_MAP.set(this.nativeElement, this.display);
      }
      this.init();
      // set the default to show unless explicitly overridden
      const defaultValue = this.marshal.getValue(this.nativeElement, this.DIRECTIVE_KEY, '');
      if (defaultValue === undefined || defaultValue === '') {
        this.setValue(true, '');
      } else {
        this.triggerUpdate();
      }
    }
    /**
     * On changes to any @Input properties...
     * Default to use the non-responsive Input value ('fxShow')
     * Then conditionally override with the mq-activated Input's current value
     */
    ngOnChanges(changes) {
      Object.keys(changes).forEach(key => {
        if (this.inputs.indexOf(key) !== -1) {
          const inputKey = key.split('.');
          const bp = inputKey.slice(1).join('.');
          const inputValue = changes[key].currentValue;
          let shouldShow = inputValue !== '' ? inputValue !== 0 ? coerceBooleanProperty(inputValue) : false : true;
          if (inputKey[0] === 'fxHide') {
            shouldShow = !shouldShow;
          }
          this.setValue(shouldShow, bp);
        }
      });
    }
    // *********************************************
    // Protected methods
    // *********************************************
    /**
     *  Watch for these extra triggers to update fxShow, fxHide stylings
     */
    trackExtraTriggers() {
      this.hasLayout = this.marshal.hasValue(this.nativeElement, 'layout');
      ['layout', 'layout-align'].forEach(key => {
        this.marshal.trackValue(this.nativeElement, key).pipe(takeUntil(this.destroySubject)).subscribe(this.triggerUpdate.bind(this));
      });
    }
    /**
     * Override accessor to the current HTMLElement's `display` style
     * Note: Show/Hide will not change the display to 'flex' but will set it to 'block'
     * unless it was already explicitly specified inline or in a CSS stylesheet.
     */
    getDisplayStyle() {
      return this.hasLayout || this.hasFlexChild && this.layoutConfig.addFlexToParent ? 'flex' : this.styler.lookupStyle(this.nativeElement, 'display', true);
    }
    /** Validate the visibility value and then update the host's inline display style */
    updateWithValue(value = true) {
      if (value === '') {
        return;
      }
      const isServer = isPlatformServer(this.platformId);
      this.addStyles(value ? 'true' : 'false', {
        display: this.display,
        isServer
      });
      if (isServer && this.serverModuleLoaded) {
        this.nativeElement.style.setProperty('display', '');
      }
      this.marshal.triggerUpdate(this.parentElement, 'layout-gap');
    }
    static {
      this.ɵfac = function ShowHideDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ShowHideDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(ShowHideStyleBuilder), i0.ɵɵdirectiveInject(i1.StyleUtils), i0.ɵɵdirectiveInject(i1.MediaMarshaller), i0.ɵɵdirectiveInject(LAYOUT_CONFIG), i0.ɵɵdirectiveInject(PLATFORM_ID), i0.ɵɵdirectiveInject(SERVER_TOKEN));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: ShowHideDirective,
        features: [i0.ɵɵInheritDefinitionFeature, i0.ɵɵNgOnChangesFeature]
      });
    }
  }
  return ShowHideDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const DISPLAY_MAP = new WeakMap();
const inputs$1 = ['fxShow', 'fxShow.print', 'fxShow.xs', 'fxShow.sm', 'fxShow.md', 'fxShow.lg', 'fxShow.xl', 'fxShow.lt-sm', 'fxShow.lt-md', 'fxShow.lt-lg', 'fxShow.lt-xl', 'fxShow.gt-xs', 'fxShow.gt-sm', 'fxShow.gt-md', 'fxShow.gt-lg', 'fxHide', 'fxHide.print', 'fxHide.xs', 'fxHide.sm', 'fxHide.md', 'fxHide.lg', 'fxHide.xl', 'fxHide.lt-sm', 'fxHide.lt-md', 'fxHide.lt-lg', 'fxHide.lt-xl', 'fxHide.gt-xs', 'fxHide.gt-sm', 'fxHide.gt-md', 'fxHide.gt-lg'];
const selector$1 = `
  [fxShow], [fxShow.print],
  [fxShow.xs], [fxShow.sm], [fxShow.md], [fxShow.lg], [fxShow.xl],
  [fxShow.lt-sm], [fxShow.lt-md], [fxShow.lt-lg], [fxShow.lt-xl],
  [fxShow.gt-xs], [fxShow.gt-sm], [fxShow.gt-md], [fxShow.gt-lg],
  [fxHide], [fxHide.print],
  [fxHide.xs], [fxHide.sm], [fxHide.md], [fxHide.lg], [fxHide.xl],
  [fxHide.lt-sm], [fxHide.lt-md], [fxHide.lt-lg], [fxHide.lt-xl],
  [fxHide.gt-xs], [fxHide.gt-sm], [fxHide.gt-md], [fxHide.gt-lg]
`;
/**
 * 'show' Layout API directive
 */
let DefaultShowHideDirective = /*#__PURE__*/(() => {
  class DefaultShowHideDirective extends ShowHideDirective {
    constructor() {
      super(...arguments);
      this.inputs = inputs$1;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵDefaultShowHideDirective_BaseFactory;
        return function DefaultShowHideDirective_Factory(__ngFactoryType__) {
          return (ɵDefaultShowHideDirective_BaseFactory || (ɵDefaultShowHideDirective_BaseFactory = i0.ɵɵgetInheritedFactory(DefaultShowHideDirective)))(__ngFactoryType__ || DefaultShowHideDirective);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DefaultShowHideDirective,
        selectors: [["", "fxShow", ""], ["", "fxShow.print", ""], ["", "fxShow.xs", ""], ["", "fxShow.sm", ""], ["", "fxShow.md", ""], ["", "fxShow.lg", ""], ["", "fxShow.xl", ""], ["", "fxShow.lt-sm", ""], ["", "fxShow.lt-md", ""], ["", "fxShow.lt-lg", ""], ["", "fxShow.lt-xl", ""], ["", "fxShow.gt-xs", ""], ["", "fxShow.gt-sm", ""], ["", "fxShow.gt-md", ""], ["", "fxShow.gt-lg", ""], ["", "fxHide", ""], ["", "fxHide.print", ""], ["", "fxHide.xs", ""], ["", "fxHide.sm", ""], ["", "fxHide.md", ""], ["", "fxHide.lg", ""], ["", "fxHide.xl", ""], ["", "fxHide.lt-sm", ""], ["", "fxHide.lt-md", ""], ["", "fxHide.lt-lg", ""], ["", "fxHide.lt-xl", ""], ["", "fxHide.gt-xs", ""], ["", "fxHide.gt-sm", ""], ["", "fxHide.gt-md", ""], ["", "fxHide.gt-lg", ""]],
        inputs: {
          fxShow: "fxShow",
          "fxShow.print": "fxShow.print",
          "fxShow.xs": "fxShow.xs",
          "fxShow.sm": "fxShow.sm",
          "fxShow.md": "fxShow.md",
          "fxShow.lg": "fxShow.lg",
          "fxShow.xl": "fxShow.xl",
          "fxShow.lt-sm": "fxShow.lt-sm",
          "fxShow.lt-md": "fxShow.lt-md",
          "fxShow.lt-lg": "fxShow.lt-lg",
          "fxShow.lt-xl": "fxShow.lt-xl",
          "fxShow.gt-xs": "fxShow.gt-xs",
          "fxShow.gt-sm": "fxShow.gt-sm",
          "fxShow.gt-md": "fxShow.gt-md",
          "fxShow.gt-lg": "fxShow.gt-lg",
          fxHide: "fxHide",
          "fxHide.print": "fxHide.print",
          "fxHide.xs": "fxHide.xs",
          "fxHide.sm": "fxHide.sm",
          "fxHide.md": "fxHide.md",
          "fxHide.lg": "fxHide.lg",
          "fxHide.xl": "fxHide.xl",
          "fxHide.lt-sm": "fxHide.lt-sm",
          "fxHide.lt-md": "fxHide.lt-md",
          "fxHide.lt-lg": "fxHide.lt-lg",
          "fxHide.lt-xl": "fxHide.lt-xl",
          "fxHide.gt-xs": "fxHide.gt-xs",
          "fxHide.gt-sm": "fxHide.gt-sm",
          "fxHide.gt-md": "fxHide.gt-md",
          "fxHide.gt-lg": "fxHide.gt-lg"
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return DefaultShowHideDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
/** NgStyle allowed inputs */
class NgStyleKeyValue {
  constructor(key, value, noQuotes = true) {
    this.key = key;
    this.value = value;
    this.key = noQuotes ? key.replace(/['"]/g, '').trim() : key.trim();
    this.value = noQuotes ? value.replace(/['"]/g, '').trim() : value.trim();
    this.value = this.value.replace(/;/, '');
  }
}
function getType(target) {
  let what = typeof target;
  if (what === 'object') {
    return target.constructor === Array ? 'array' : target.constructor === Set ? 'set' : 'object';
  }
  return what;
}
/**
 * Split string of key:value pairs into Array of k-v pairs
 * e.g.  'key:value; key:value; key:value;' -> ['key:value',...]
 */
function buildRawList(source, delimiter = ';') {
  return String(source).trim().split(delimiter).map(val => val.trim()).filter(val => val !== '');
}
/** Convert array of key:value strings to a iterable map object */
function buildMapFromList$1(styles, sanitize) {
  const sanitizeValue = it => {
    if (sanitize) {
      it.value = sanitize(it.value);
    }
    return it;
  };
  return styles.map(stringToKeyValue).filter(entry => !!entry).map(sanitizeValue).reduce(keyValuesToMap, {});
}
/** Convert Set<string> or raw Object to an iterable NgStyleMap */
function buildMapFromSet(source, sanitize) {
  let list = [];
  if (getType(source) === 'set') {
    source.forEach(entry => list.push(entry));
  } else {
    Object.keys(source).forEach(key => {
      list.push(`${key}:${source[key]}`);
    });
  }
  return buildMapFromList$1(list, sanitize);
}
/** Convert 'key:value' -> [key, value] */
function stringToKeyValue(it) {
  const [key, ...vals] = it.split(':');
  return new NgStyleKeyValue(key, vals.join(':'));
}
/** Convert [ [key,value] ] -> { key : value } */
function keyValuesToMap(map, entry) {
  if (!!entry.key) {
    map[entry.key] = entry.value;
  }
  return map;
}

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
let StyleDirective = /*#__PURE__*/(() => {
  class StyleDirective extends BaseDirective2 {
    constructor(elementRef, styler, marshal, sanitizer, differs, renderer2, ngStyleInstance, serverLoaded, platformId) {
      super(elementRef, null, styler, marshal);
      this.sanitizer = sanitizer;
      this.ngStyleInstance = ngStyleInstance;
      this.DIRECTIVE_KEY = 'ngStyle';
      if (!this.ngStyleInstance) {
        // Create an instance NgStyle Directive instance only if `ngStyle=""` has NOT been
        // defined on the same host element; since the responsive variations may be defined...
        this.ngStyleInstance = new NgStyle(elementRef, differs, renderer2);
      }
      this.init();
      const styles = this.nativeElement.getAttribute('style') ?? '';
      this.fallbackStyles = this.buildStyleMap(styles);
      this.isServer = serverLoaded && isPlatformServer(platformId);
    }
    /** Add generated styles */
    updateWithValue(value) {
      const styles = this.buildStyleMap(value);
      this.ngStyleInstance.ngStyle = {
        ...this.fallbackStyles,
        ...styles
      };
      if (this.isServer) {
        this.applyStyleToElement(styles);
      }
      this.ngStyleInstance.ngDoCheck();
    }
    /** Remove generated styles */
    clearStyles() {
      this.ngStyleInstance.ngStyle = this.fallbackStyles;
      this.ngStyleInstance.ngDoCheck();
    }
    /**
     * Convert raw strings to ngStyleMap; which is required by ngStyle
     * NOTE: Raw string key-value pairs MUST be delimited by `;`
     *       Comma-delimiters are not supported due to complexities of
     *       possible style values such as `rgba(x,x,x,x)` and others
     */
    buildStyleMap(styles) {
      // Always safe-guard (aka sanitize) style property values
      const sanitizer = val => this.sanitizer.sanitize(SecurityContext.STYLE, val) ?? '';
      if (styles) {
        switch (getType(styles)) {
          case 'string':
            return buildMapFromList(buildRawList(styles), sanitizer);
          case 'array':
            return buildMapFromList(styles, sanitizer);
          case 'set':
            return buildMapFromSet(styles, sanitizer);
          default:
            return buildMapFromSet(styles, sanitizer);
        }
      }
      return {};
    }
    // ******************************************************************
    // Lifecycle Hooks
    // ******************************************************************
    /** For ChangeDetectionStrategy.onPush and ngOnChanges() updates */
    ngDoCheck() {
      this.ngStyleInstance.ngDoCheck();
    }
    static {
      this.ɵfac = function StyleDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || StyleDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1.StyleUtils), i0.ɵɵdirectiveInject(i1.MediaMarshaller), i0.ɵɵdirectiveInject(i2$1.DomSanitizer), i0.ɵɵdirectiveInject(i0.KeyValueDiffers), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i2.NgStyle, 10), i0.ɵɵdirectiveInject(SERVER_TOKEN), i0.ɵɵdirectiveInject(PLATFORM_ID));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: StyleDirective,
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return StyleDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const inputs = ['ngStyle', 'ngStyle.xs', 'ngStyle.sm', 'ngStyle.md', 'ngStyle.lg', 'ngStyle.xl', 'ngStyle.lt-sm', 'ngStyle.lt-md', 'ngStyle.lt-lg', 'ngStyle.lt-xl', 'ngStyle.gt-xs', 'ngStyle.gt-sm', 'ngStyle.gt-md', 'ngStyle.gt-lg'];
const selector = `
  [ngStyle],
  [ngStyle.xs], [ngStyle.sm], [ngStyle.md], [ngStyle.lg], [ngStyle.xl],
  [ngStyle.lt-sm], [ngStyle.lt-md], [ngStyle.lt-lg], [ngStyle.lt-xl],
  [ngStyle.gt-xs], [ngStyle.gt-sm], [ngStyle.gt-md], [ngStyle.gt-lg]
`;
/**
 * Directive to add responsive support for ngStyle.
 *
 */
let DefaultStyleDirective = /*#__PURE__*/(() => {
  class DefaultStyleDirective extends StyleDirective {
    constructor() {
      super(...arguments);
      this.inputs = inputs;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵDefaultStyleDirective_BaseFactory;
        return function DefaultStyleDirective_Factory(__ngFactoryType__) {
          return (ɵDefaultStyleDirective_BaseFactory || (ɵDefaultStyleDirective_BaseFactory = i0.ɵɵgetInheritedFactory(DefaultStyleDirective)))(__ngFactoryType__ || DefaultStyleDirective);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DefaultStyleDirective,
        selectors: [["", "ngStyle", ""], ["", "ngStyle.xs", ""], ["", "ngStyle.sm", ""], ["", "ngStyle.md", ""], ["", "ngStyle.lg", ""], ["", "ngStyle.xl", ""], ["", "ngStyle.lt-sm", ""], ["", "ngStyle.lt-md", ""], ["", "ngStyle.lt-lg", ""], ["", "ngStyle.lt-xl", ""], ["", "ngStyle.gt-xs", ""], ["", "ngStyle.gt-sm", ""], ["", "ngStyle.gt-md", ""], ["", "ngStyle.gt-lg", ""]],
        inputs: {
          ngStyle: "ngStyle",
          "ngStyle.xs": "ngStyle.xs",
          "ngStyle.sm": "ngStyle.sm",
          "ngStyle.md": "ngStyle.md",
          "ngStyle.lg": "ngStyle.lg",
          "ngStyle.xl": "ngStyle.xl",
          "ngStyle.lt-sm": "ngStyle.lt-sm",
          "ngStyle.lt-md": "ngStyle.lt-md",
          "ngStyle.lt-lg": "ngStyle.lt-lg",
          "ngStyle.lt-xl": "ngStyle.lt-xl",
          "ngStyle.gt-xs": "ngStyle.gt-xs",
          "ngStyle.gt-sm": "ngStyle.gt-sm",
          "ngStyle.gt-md": "ngStyle.gt-md",
          "ngStyle.gt-lg": "ngStyle.gt-lg"
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return DefaultStyleDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
/** Build a styles map from a list of styles, while sanitizing bad values first */
function buildMapFromList(styles, sanitize) {
  const sanitizeValue = it => {
    if (sanitize) {
      it.value = sanitize(it.value);
    }
    return it;
  };
  return styles.map(stringToKeyValue).filter(entry => !!entry).map(sanitizeValue).reduce(keyValuesToMap, {});
}

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
const ALL_DIRECTIVES = [DefaultShowHideDirective, DefaultClassDirective, DefaultStyleDirective, DefaultImgSrcDirective];
/**
 * *****************************************************************
 * Define module for the Extended API
 * *****************************************************************
 */
let ExtendedModule = /*#__PURE__*/(() => {
  class ExtendedModule {
    static {
      this.ɵfac = function ExtendedModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ExtendedModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: ExtendedModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [CoreModule]
      });
    }
  }
  return ExtendedModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ClassDirective, DefaultClassDirective, DefaultImgSrcDirective, DefaultShowHideDirective, DefaultStyleDirective, ExtendedModule, ImgSrcDirective, ImgSrcStyleBuilder, ShowHideDirective, ShowHideStyleBuilder, StyleDirective };
